'use client';

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth';
import { useAuth } from '@/lib/contexts/auth-context';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, Check, ChevronDown, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CreateTenantDialog } from '@/components/create-tenant-dialog';
import { apiErrorMessage } from '@/lib/api/errors';

export function TenantSwitcher() {
  const { loginWithTokens } = useAuth();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data: tenantsData, isLoading } = useQuery({
    queryKey: ['user-tenants'],
    queryFn: () => authApi.getTenants(),
  });

  const switchMutation = useMutation({
    mutationFn: (tenantId: string) => authApi.switchTenant({ tenant_id: tenantId }),
    onSuccess: async (data) => {
      // Update tokens in local storage
      await loginWithTokens(data.access_token, data.refresh_token);
      // Invalidate all queries to refetch data for new tenant
      queryClient.invalidateQueries();
      toast.success(data.message);
      // Reload the page to reset all state
      window.location.reload();
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Failed to switch tenant'));
    },
  });

  const activeTenant = tenantsData?.tenants.find(
    (t) => t.id === tenantsData.active_tenant_id
  );

  if (isLoading) {
    return (
      <Button variant="outline" className="w-full justify-start" disabled>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading...
      </Button>
    );
  }

  if (!tenantsData || tenantsData.tenants.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between"
            disabled={switchMutation.isPending}
            title={activeTenant?.name}
          >
            {/* min-w-0 lets the name shrink and truncate; without it a long
                workspace name ran out of the button. */}
            <div className="flex min-w-0 items-center">
              <Building2 className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">{activeTenant?.name || 'Select workspace'}</span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {tenantsData.tenants.map((tenant) => (
            <DropdownMenuItem
              key={tenant.id}
              onClick={() => {
                if (tenant.id !== activeTenant?.id) {
                  switchMutation.mutate(tenant.id);
                }
              }}
              disabled={switchMutation.isPending}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between gap-2 w-full min-w-0">
                <div className="flex min-w-0 flex-col">
                  <span className="truncate font-medium" title={tenant.name}>{tenant.name}</span>
                  <span className="text-xs text-muted-foreground capitalize">
                    {tenant.role}
                  </span>
                </div>
                {tenant.id === activeTenant?.id && (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setCreateDialogOpen(true)}
            className="cursor-pointer"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateTenantDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}
